import { setImportedRecipe } from "@/lib/import-recipe-store";
import { Brand, Neutrals } from "@/constants/theme";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useImportRecipe } from "@/hooks/useImportRecipe";
import { useRecipeSuggestions } from "@/hooks/useRecipeSuggestions";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import React, { useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

/** Recipe URLs we know parse reliably (Schema.org / importer-friendly). Used for random suggestion. */
const KNOWN_GOOD_RECIPES: { label: string; url: string }[] = [
  { label: "Vegan gluten-free chocolate chip cookies (Minimalist Baker)", url: "https://minimalistbaker.com/vegan-gluten-free-chocolate-chip-cookies/" },
  { label: "1-Bowl vegan chocolate chip cookies (Minimalist Baker)", url: "https://minimalistbaker.com/1-bowl-vegan-chocolate-chip-cookies/" },
  { label: "Creamy white bean and spinach dip (Budget Bytes)", url: "https://www.budgetbytes.com/creamy-white-bean-and-spinach-dip/" },
  { label: "Perfect guacamole (Simply Recipes)", url: "https://www.simplyrecipes.com/recipes/perfect_guacamole/" },
  { label: "Classic spaghetti carbonara (BBC Good Food)", url: "https://www.bbcgoodfood.com/recipes/classic-spaghetti-carbonara" },
  { label: "Crispy roasted potatoes (Serious Eats)", url: "https://www.seriouseats.com/crispy-roasted-potatoes-recipe" },
];

/** Site roots for “browse then paste” – opening in browser. */
const POPULAR_SITES: { name: string; url: string }[] = [
  { name: "AllRecipes", url: "https://www.allrecipes.com" },
  { name: "Food Network", url: "https://www.foodnetwork.com" },
  { name: "Serious Eats", url: "https://www.seriouseats.com" },
  { name: "Epicurious", url: "https://www.epicurious.com" },
  { name: "Tasty", url: "https://tasty.co" },
  { name: "Simply Recipes", url: "https://www.simplyrecipes.com" },
  { name: "Budget Bytes", url: "https://www.budgetbytes.com" },
  { name: "BBC Good Food", url: "https://www.bbcgoodfood.com" },
  { name: "Minimalist Baker", url: "https://minimalistbaker.com" },
];

function pickRandomRecipe(suggestions: { label: string; url: string }[]): { label: string; url: string } {
  const pool = suggestions.length > 0 ? suggestions : KNOWN_GOOD_RECIPES;
  return pool[Math.floor(Math.random() * pool.length)];
}

export default function ImportRecipeScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { state, importRecipe, reset } = useImportRecipe();
  const suggestions = useRecipeSuggestions();
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (state.status !== "success") return;
    const data = state.data;
    setImportedRecipe(data);
    reset();
    router.replace("/(tabs)/recipes/new");
  }, [state, reset, router]);

  const handleImport = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    await importRecipe(trimmed);
  };

  const handleRandomRecipe = useCallback(() => {
    const { url: recipeUrl } = pickRandomRecipe(suggestions);
    setUrl(recipeUrl);
  }, [suggestions]);

  const openSiteInBrowser = useCallback((siteUrl: string) => {
    Linking.openURL(siteUrl);
  }, []);

  const isLoading = state.status === "loading";

  return (
    <View style={styles.container}>
      <LinearGradient colors={[...Brand.primaryGradient]} style={styles.background} />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.content, { paddingTop: 16 + insets.top }]}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.header}>
          <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
            <Ionicons name="arrow-back" size={20} color={Brand.primary} />
            <Text style={styles.backText}>Back</Text>
          </TouchableOpacity>
          <Text style={styles.title}>Import from URL</Text>
          <Text style={styles.subtitle}>Paste a recipe link to import ingredients and steps</Text>
        </View>

        <Card style={styles.card}>
          <CardHeader>
            <CardTitle>
              <Text style={styles.cardTitleText}>Recipe URL</Text>
            </CardTitle>
          </CardHeader>
          <CardContent style={styles.cardContent}>
            <Input
              value={url}
              onChangeText={setUrl}
              placeholder="https://www.allrecipes.com/recipe/..."
              keyboardType="url"
              autoCapitalize="none"
              autoCorrect={false}
              autoFocus
              editable={!isLoading}
              containerStyle={styles.input}
            />
            {state.status === "error" && (
              <View style={styles.errorRow}>
                <Ionicons name="warning-outline" size={18} color={Brand.destructive} />
                <Text style={styles.errorText}>{state.message}</Text>
              </View>
            )}
            {state.status === "loading" && (
              <View style={styles.loadingRow}>
                <ActivityIndicator size="small" color={Brand.primary} />
                <Text style={styles.stageText}>{state.stage}</Text>
              </View>
            )}
            <Button
              onPress={handleImport}
              style={styles.importButton}
              disabled={!url.trim() || isLoading}
              loading={isLoading}
            >
              Import
            </Button>
          </CardContent>
        </Card>

        <Card style={styles.card}>
          <CardHeader>
            <CardTitle>
              <Text style={styles.cardTitleText}>Try a random recipe</Text>
            </CardTitle>
          </CardHeader>
          <CardContent style={styles.cardContent}>
            <Text style={styles.sectionHint}>
              Pre-fill the URL with a recipe we know imports reliably. Tap Import above to use it.
            </Text>
            <TouchableOpacity
              onPress={handleRandomRecipe}
              style={styles.randomButton}
              disabled={isLoading}
            >
              <Ionicons name="shuffle-outline" size={18} color={Brand.primary} />
              <Text style={styles.randomButtonText}>Suggest a recipe</Text>
            </TouchableOpacity>
          </CardContent>
        </Card>

        <Card style={styles.card}>
          <CardHeader>
            <CardTitle>
              <Text style={styles.cardTitleText}>Popular recipe sites</Text>
            </CardTitle>
          </CardHeader>
          <CardContent style={styles.cardContent}>
            <Text style={styles.sectionHint}>
              Open a site in your browser, find a recipe, then copy its link and paste it above.
            </Text>
            <View style={styles.chips}>
              {POPULAR_SITES.map((site) => (
                <TouchableOpacity
                  key={site.name}
                  onPress={() => openSiteInBrowser(site.url)}
                  style={styles.chip}
                  disabled={isLoading}
                >
                  <Text style={styles.chipText}>{site.name}</Text>
                  <Ionicons name="open-outline" size={14} color={Neutrals.textSecondary} />
                </TouchableOpacity>
              ))}
            </View>
          </CardContent>
        </Card>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  background: { position: "absolute", left: 0, right: 0, top: 0, bottom: 0 },
  scrollView: { flex: 1 },
  content: {
    paddingHorizontal: 16,
    paddingBottom: 40,
    maxWidth: 864,
    alignSelf: "center",
    width: "100%",
  },
  header: { marginBottom: 24 },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 6,
    marginBottom: 12,
    paddingVertical: 4,
  },
  backText: { color: Brand.primary, fontSize: 16, fontWeight: "500" },
  title: { fontSize: 28, fontWeight: "bold", color: Neutrals.textPrimary },
  subtitle: { fontSize: 15, color: Neutrals.textSecondary, marginTop: 4 },
  card: { marginBottom: 16 },
  cardContent: { gap: 12 },
  cardTitleText: { fontSize: 18, fontWeight: "600", color: Neutrals.textPrimary },
  input: {},
  errorRow: { flexDirection: "row", alignItems: "center", gap: 8 },
  errorText: { color: Brand.destructive, fontSize: 14, flex: 1 },
  loadingRow: { flexDirection: "row", alignItems: "center", gap: 10 },
  stageText: { color: Neutrals.textSecondary, fontSize: 14 },
  importButton: { marginTop: 4 },
  sectionHint: { fontSize: 13, color: Neutrals.textSecondary, marginBottom: 12 },
  randomButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    backgroundColor: Brand.primaryLighter,
    borderWidth: 1,
    borderColor: Brand.primaryLight,
    alignSelf: "flex-start",
  },
  randomButtonText: { fontSize: 15, color: Brand.primary, fontWeight: "600" },
  chips: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: Brand.primaryLighter,
    borderWidth: 1,
    borderColor: Brand.primaryLight,
  },
  chipText: { fontSize: 14, color: Neutrals.textPrimary, fontWeight: "500" },
});
